import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import {
  pmsApprovalStatusField,
  pmsAssignedUserField,
  pmsCrewRankField,
  pmsEquipmentForVesselField,
  pmsEquipmentInstanceField,
  pmsMaintenanceJobField,
  pmsMaintenanceScheduleField,
  pmsVesselField,
  pmsWorkOrderSourceField,
  pmsWorkOrderStatusField} from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsWorkOrder } from './useData';

function stripWorkOrderPayload(data: Partial<PmsWorkOrder>): Record<string, unknown> {
  const {
    vessel,
    equipment,
    equipmentInstance,
    maintenanceJob,
    maintenanceSchedule,
    status,
    sourceType,
    approvalStatus,
    assignedRank,
    assignedUser,
    createdBy,
    completedBy,
    reviewedBy,
    approvedBy,
    steps,
    measurements,
    spareParts,
    id,
    createdAt,
    updatedAt,
    isLocked,
    lockedAt,
    ...payload
  } = data as Record<string, unknown>;
  return payload;
}

export const moduleConfig = {
  module: {
    title: "Work orders",
    permissionModuleKey: 'pms_work_orders',
    route: {
      list: '/pms/pms-work-orders',
      new: '/pms/pms-work-orders/new',
      detail: (id: number) => `/pms/pms-work-orders/${id}`,
      edit: (id: number) => `/pms/pms-work-orders/${id}/edit`},
    api: { list: '/pms-work-orders', detail: (id: number) => `/pms-work-orders/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsWorkOrder[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-work-orders/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsWorkOrder) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsWorkOrder } = await import('../../../services/mutations');
      await deletePmsWorkOrder(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Work orders',
    getTitle: (item: PmsWorkOrder) =>
      item.workOrderNumber
        ? `${String(item.workOrderNumber)}${item.title ? ` — ${String(item.title)}` : ''}`
        : item.title
          ? String(item.title)
          : `#${item.id}`},
  form: {
    new: { title: 'New work order', submitText: 'Create' },
    edit: { title: 'Edit work order', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-5xl',
    create: async (data: Partial<PmsWorkOrder>) => {
      const { createPmsWorkOrder } = await import('../../../services/mutations');
      return createPmsWorkOrder(stripWorkOrderPayload(data));
    },
    update: async (id: number, data: Partial<PmsWorkOrder>) => {
      const { updatePmsWorkOrder } = await import('../../../services/mutations');
      return updatePmsWorkOrder(id, stripWorkOrderPayload(data));
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Vessel where the work will be performed',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('equipmentId', '');
        setFieldValue('equipmentInstanceId', '');
        setFieldValue('maintenanceJobId', '');
        setFieldValue('maintenanceScheduleId', '');
      }},
    {
      ...pmsEquipmentForVesselField(true),
      helperText: 'Equipment slot this work order targets',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('equipmentInstanceId', '');
        setFieldValue('maintenanceJobId', '');
        setFieldValue('maintenanceScheduleId', '');
      }},
    {
      ...pmsEquipmentInstanceField(false),
      helperText: 'Optional physical serial currently in the slot'},
    {
      ...pmsMaintenanceJobField(false),
      helperText: 'Optional PMS job that generated this work order',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('maintenanceScheduleId', '');
      }},
    {
      ...pmsMaintenanceScheduleField(false),
      helperText: 'Optional schedule interval that triggered the due'},
    {
      name: 'workOrderNumber',
      label: 'Work order number',
      type: 'text',
      required: true,
      placeholder: 'e.g. WO-2026-00142',
      validation: { required: true }},
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Short description of the work',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Scope, precautions, and references for the crew'},
    {
      ...pmsWorkOrderStatusField(true),
      helperText: 'Current workflow status'},
    {
      ...pmsWorkOrderSourceField(true),
      helperText: 'How this work order was created'},
    {
      ...pmsApprovalStatusField(true),
      helperText: 'Approval state for completion / close-out'},
    {
      name: 'priority',
      label: 'Priority',
      type: 'number',
      min: 1,
      max: 5,
      placeholder: '1 = highest, 5 = lowest',
      helperText: 'Default is 3'},
    pmsCrewRankField('assignedRankId', 'Assigned rank', false),
    {
      ...pmsAssignedUserField(false),
      helperText: 'Optional named assignee'},
    {
      name: 'snapshotEquipmentCode',
      label: 'Equipment code',
      type: 'text',
      required: true,
      validation: { required: true },
      helperText: 'Frozen at creation — kept for history if masters change'},
    {
      name: 'snapshotEquipmentName',
      label: 'Equipment name',
      type: 'text',
      required: true,
      validation: { required: true }},
    {
      name: 'snapshotEquipmentSerial',
      label: 'Serial number',
      type: 'text',
      placeholder: 'Optional'},
    {
      name: 'snapshotJobCode',
      label: 'Job code',
      type: 'text',
      placeholder: 'Optional'},
    {
      name: 'snapshotJobTitle',
      label: 'Job title',
      type: 'text',
      required: true,
      validation: { required: true }},
    {
      name: 'completionNotes',
      label: 'Completion notes',
      type: 'textarea',
      rows: 3,
      placeholder: 'Findings, measurements, and close-out comments'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'scope',
        title: 'Scope',
        description: 'Vessel, equipment, and the PMS job/schedule this work comes from.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'vesselId',
          'equipmentId',
          'equipmentInstanceId',
          'maintenanceJobId',
          'maintenanceScheduleId',
        ],
        fieldSpans: {
          vesselId: 'md:col-span-4',
          equipmentId: 'md:col-span-4',
          equipmentInstanceId: 'md:col-span-4',
          maintenanceJobId: 'md:col-span-6',
          maintenanceScheduleId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'work-order',
        title: 'Work order',
        description: 'Number, title, and instructions for the crew.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['workOrderNumber', 'title', 'description'],
        fieldSpans: {
          workOrderNumber: 'md:col-span-4',
          title: 'md:col-span-8',
          description: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'workflow',
        title: 'Workflow',
        description: 'Status, source, approval, and priority.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['statusId', 'sourceTypeId', 'approvalStatusId', 'priority'],
        fieldSpans: {
          statusId: 'md:col-span-3',
          sourceTypeId: 'md:col-span-3',
          approvalStatusId: 'md:col-span-3',
          priority: 'md:col-span-3'},
        collapsible: false},
      {
        id: 'assignment',
        title: 'Assignment',
        description: 'Who should carry out the work on board.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['assignedRankId', 'assignedUserId'],
        fieldSpans: {
          assignedRankId: 'md:col-span-6',
          assignedUserId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'snapshot',
        title: 'Snapshot at creation',
        description:
          'Frozen copy of equipment/job labels for history. Prefer leaving these as captured when the WO was opened.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'snapshotEquipmentCode',
          'snapshotEquipmentName',
          'snapshotEquipmentSerial',
          'snapshotJobCode',
          'snapshotJobTitle',
        ],
        fieldSpans: {
          snapshotEquipmentCode: 'md:col-span-4',
          snapshotEquipmentName: 'md:col-span-5',
          snapshotEquipmentSerial: 'md:col-span-3',
          snapshotJobCode: 'md:col-span-4',
          snapshotJobTitle: 'md:col-span-8'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'completion',
        title: 'Completion',
        description: 'Notes captured when closing out the work order.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['completionNotes'],
        fieldSpans: { completionNotes: 'md:col-span-12' },
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const relName = (
  rel: { code?: string | null; name?: string | null; title?: string | null; vesselName?: string | null; abrv?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  if (rel?.title) return rel.title;
  if (rel?.vesselName) return rel.vesselName;
  return rel?.abrv ?? rel?.code ?? (id != null ? `#${id}` : '-');
};

export const labelVessel = (item: PmsWorkOrder) => {
  const v = item.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

export const labelEquipment = (item: PmsWorkOrder) =>
  relName(item.equipment as { name?: string } | null, item.equipmentId as number | null);

export const labelInstance = (item: PmsWorkOrder) => {
  const inst = item.equipmentInstance as { serialNumber?: string; name?: string } | null | undefined;
  if (inst?.serialNumber) return inst.serialNumber;
  if (inst?.name) return inst.name;
  return item.equipmentInstanceId != null ? `#${item.equipmentInstanceId}` : '-';
};

export const labelJob = (item: PmsWorkOrder) =>
  relName(
    item.maintenanceJob as { title?: string; name?: string } | null,
    item.maintenanceJobId as number | null,
  );

/** Fields editable on the work-order edit split page (right pane). */
export const workOrderEditFields: FieldConfig[] = [
  {
    ...pmsWorkOrderStatusField(true),
    helperText: 'Current workflow status'},
  {
    ...pmsApprovalStatusField(true),
    helperText: 'Approval state for completion / close-out'},
  {
    name: 'priority',
    label: 'Priority',
    type: 'number',
    min: 1,
    max: 5,
    placeholder: '1 = highest, 5 = lowest',
    helperText: '1 = highest, 5 = lowest'},
  pmsCrewRankField('assignedRankId', 'Assigned rank', false),
  {
    ...pmsAssignedUserField(false),
    helperText: 'Optional named assignee'},
  {
    name: 'description',
    label: 'Work instructions',
    type: 'textarea',
    rows: 4,
    placeholder: 'Scope, precautions, and references for the crew'},
  {
    name: 'completionNotes',
    label: 'Completion notes',
    type: 'textarea',
    rows: 4,
    placeholder: 'Findings, measurements, and close-out comments'},
];

export const workOrderEditFormLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'workflow',
      title: 'Workflow',
      description: 'Status and priority as work progresses.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['statusId', 'approvalStatusId', 'priority'],
      fieldSpans: {
        statusId: 'md:col-span-4',
        approvalStatusId: 'md:col-span-4',
        priority: 'md:col-span-4'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'assignment',
      title: 'Assignment',
      description: 'Who owns the work on board.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['assignedRankId', 'assignedUserId'],
      fieldSpans: {
        assignedRankId: 'md:col-span-6',
        assignedUserId: 'md:col-span-6'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'instructions',
      title: 'Instructions & close-out',
      description: 'Crew guidance and completion notes.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['description', 'completionNotes'],
      fieldSpans: {
        description: 'md:col-span-12',
        completionNotes: 'md:col-span-12'},
      collapsible: false,
      className: 'shadow-none'},
  ]};

/** Left pane on create — scope that becomes the locked dossier after save. */
export const workOrderCreateScopeFields: FieldConfig[] = [
  {
    ...pmsVesselField(true),
    helperText: 'Vessel where the work will be performed',
    onChangeSideEffect: (_value, setFieldValue) => {
      setFieldValue('equipmentId', '');
      setFieldValue('equipmentInstanceId', '');
      setFieldValue('maintenanceJobId', '');
      setFieldValue('maintenanceScheduleId', '');
      setFieldValue('snapshotEquipmentCode', '');
      setFieldValue('snapshotEquipmentName', '');
      setFieldValue('snapshotEquipmentSerial', '');
      setFieldValue('snapshotJobCode', '');
      setFieldValue('snapshotJobTitle', '');
    }},
  {
    ...pmsEquipmentForVesselField(true),
    helperText: 'Equipment slot this work order targets',
    onChangeSideEffect: (_value, setFieldValue) => {
      setFieldValue('equipmentInstanceId', '');
      setFieldValue('maintenanceJobId', '');
      setFieldValue('maintenanceScheduleId', '');
      setFieldValue('snapshotEquipmentCode', '');
      setFieldValue('snapshotEquipmentName', '');
      setFieldValue('snapshotEquipmentSerial', '');
      setFieldValue('snapshotJobCode', '');
      setFieldValue('snapshotJobTitle', '');
    }},
  {
    ...pmsEquipmentInstanceField(false),
    helperText: 'Optional physical serial currently in the slot'},
  {
    ...pmsMaintenanceJobField(false),
    helperText: 'Optional PMS job that generated this work order',
    onChangeSideEffect: (_value, setFieldValue) => {
      setFieldValue('maintenanceScheduleId', '');
      setFieldValue('snapshotJobCode', '');
      setFieldValue('snapshotJobTitle', '');
    }},
  {
    ...pmsMaintenanceScheduleField(false),
    helperText: 'Optional schedule interval that triggered the due'},
  {
    name: 'snapshotEquipmentCode',
    label: 'Equipment code',
    type: 'text',
    required: true,
    validation: { required: true },
    helperText: 'Frozen at creation for history'},
  {
    name: 'snapshotEquipmentName',
    label: 'Equipment name',
    type: 'text',
    required: true,
    validation: { required: true }},
  {
    name: 'snapshotEquipmentSerial',
    label: 'Serial number',
    type: 'text',
    placeholder: 'Optional'},
  {
    name: 'snapshotJobCode',
    label: 'Job code',
    type: 'text',
    placeholder: 'Optional'},
  {
    name: 'snapshotJobTitle',
    label: 'Job title',
    type: 'text',
    required: true,
    validation: { required: true }},
];

export const workOrderCreateScopeLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'scope',
      title: 'Scope',
      description: 'Vessel, equipment, and optional job/schedule.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: [
        'vesselId',
        'equipmentId',
        'equipmentInstanceId',
        'maintenanceJobId',
        'maintenanceScheduleId',
      ],
      fieldSpans: {
        vesselId: 'md:col-span-12',
        equipmentId: 'md:col-span-12',
        equipmentInstanceId: 'md:col-span-12',
        maintenanceJobId: 'md:col-span-12',
        maintenanceScheduleId: 'md:col-span-12'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'snapshot',
      title: 'Snapshot at creation',
      description: 'Labels frozen on the work order for history.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: [
        'snapshotEquipmentCode',
        'snapshotEquipmentName',
        'snapshotEquipmentSerial',
        'snapshotJobCode',
        'snapshotJobTitle',
      ],
      fieldSpans: {
        snapshotEquipmentCode: 'md:col-span-6',
        snapshotEquipmentName: 'md:col-span-6',
        snapshotEquipmentSerial: 'md:col-span-12',
        snapshotJobCode: 'md:col-span-4',
        snapshotJobTitle: 'md:col-span-8'},
      collapsible: false,
      className: 'shadow-none'},
  ]};

/** Right pane on create — same workspace as edit, plus identity fields. */
export const workOrderCreatePlanFields: FieldConfig[] = [
  {
    name: 'workOrderNumber',
    label: 'Work order number',
    type: 'text',
    required: true,
    placeholder: 'e.g. WO-2026-00142',
    validation: { required: true }},
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: true,
    placeholder: 'Short description of the work',
    validation: { required: true }},
  {
    ...pmsWorkOrderStatusField(true),
    helperText: 'Starting workflow status'},
  {
    ...pmsWorkOrderSourceField(true),
    helperText: 'How this work order was created'},
  {
    ...pmsApprovalStatusField(true),
    helperText: 'Approval state for completion / close-out'},
  {
    name: 'priority',
    label: 'Priority',
    type: 'number',
    min: 1,
    max: 5,
    placeholder: '1 = highest, 5 = lowest',
    helperText: '1 = highest, 5 = lowest'},
  pmsCrewRankField('assignedRankId', 'Assigned rank', false),
  {
    ...pmsAssignedUserField(false),
    helperText: 'Optional named assignee'},
  {
    name: 'description',
    label: 'Work instructions',
    type: 'textarea',
    rows: 4,
    placeholder: 'Scope, precautions, and references for the crew'},
  {
    name: 'completionNotes',
    label: 'Completion notes',
    type: 'textarea',
    rows: 4,
    placeholder: 'Findings, measurements, and close-out comments'},
];

export const workOrderCreatePlanLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'identity',
      title: 'Work order',
      description: 'Number and title for the crew.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['workOrderNumber', 'title'],
      fieldSpans: {
        workOrderNumber: 'md:col-span-5',
        title: 'md:col-span-7'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'workflow',
      title: 'Workflow',
      description: 'Status, source, and priority.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['statusId', 'sourceTypeId', 'approvalStatusId', 'priority'],
      fieldSpans: {
        statusId: 'md:col-span-6',
        sourceTypeId: 'md:col-span-6',
        approvalStatusId: 'md:col-span-6',
        priority: 'md:col-span-6'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'assignment',
      title: 'Assignment',
      description: 'Who owns the work on board.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['assignedRankId', 'assignedUserId'],
      fieldSpans: {
        assignedRankId: 'md:col-span-6',
        assignedUserId: 'md:col-span-6'},
      collapsible: false,
      className: 'shadow-none'},
    {
      id: 'instructions',
      title: 'Instructions & close-out',
      description: 'Crew guidance and completion notes.',
      icon: '',
      gridCols: 'md:grid-cols-12',
      gridAlign: 'start',
      fields: ['description', 'completionNotes'],
      fieldSpans: {
        description: 'md:col-span-12',
        completionNotes: 'md:col-span-12'},
      collapsible: false,
      className: 'shadow-none'},
  ]};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PmsWorkOrder>[] => [
  {
    key: 'workOrderNumber',
    header: 'WO #',
    sortable: true,
    width: '8.5rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'title',
    header: 'Title',
    sortable: true,
    width: '14rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    width: '9rem',
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'equipmentId',
    header: 'Equipment',
    sortable: true,
    width: '11rem',
    render: (_v, row) => <TextCell value={labelEquipment(row)} />},
  {
    key: 'equipmentInstanceId',
    header: 'Instance',
    sortable: true,
    width: '8rem',
    render: (_v, row) => <TextCell value={labelInstance(row)} />},
  {
    key: 'maintenanceJobId',
    header: 'Job',
    sortable: true,
    width: '12rem',
    render: (_v, row) => <TextCell value={labelJob(row)} />},
  {
    key: 'maintenanceScheduleId',
    header: 'Schedule',
    sortable: true,
    width: '7rem',
    render: (_v, row) => {
      const s = row.maintenanceSchedule as
        | { id?: number; scheduleType?: { name?: string; code?: string } | null; intervalValue?: number | string | null }
        | null
        | undefined;
      if (!s && row.maintenanceScheduleId == null) return <TextCell value={undefined} />;
      const typeName = s?.scheduleType?.name ?? s?.scheduleType?.code;
      const label =
        typeName && s?.intervalValue != null
          ? `${typeName} (${s.intervalValue})`
          : typeName || (row.maintenanceScheduleId != null ? `#${row.maintenanceScheduleId}` : undefined);
      return <TextCell value={label} />;
    }},
  {
    key: 'statusId',
    header: 'Status',
    sortable: true,
    width: '8rem',
    render: (_v, row) => (
      <TextCell
        value={relName(row.status as { code?: string; name?: string } | null, row.statusId as number | null)}
      />
    )},
  {
    key: 'sourceTypeId',
    header: 'Source',
    sortable: true,
    width: '8rem',
    render: (_v, row) => (
      <TextCell
        value={relName(
          row.sourceType as { code?: string; name?: string } | null,
          row.sourceTypeId as number | null,
        )}
      />
    )},
  {
    key: 'approvalStatusId',
    header: 'Approval',
    sortable: true,
    width: '8rem',
    render: (_v, row) => (
      <TextCell
        value={relName(
          row.approvalStatus as { code?: string; name?: string } | null,
          row.approvalStatusId as number | null,
        )}
      />
    )},
  {
    key: 'priority',
    header: 'Pri',
    sortable: true,
    width: '3.5rem',
    align: 'center',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'assignedRankId',
    header: 'Assigned rank',
    sortable: true,
    width: '8rem',
    render: (_v, row) => (
      <TextCell
        value={relName(
          row.assignedRank as { name?: string; abrv?: string; code?: string } | null,
          row.assignedRankId as number | null,
        )}
      />
    )},
  {
    key: 'assignedUserId',
    header: 'Assigned user',
    sortable: true,
    width: '10rem',
    render: (_v, row) => {
      const u = row.assignedUser as { userDisplayName?: string; userEmail?: string } | null | undefined;
      const label =
        u?.userDisplayName || u?.userEmail || (row.assignedUserId != null ? `#${row.assignedUserId}` : undefined);
      return <TextCell value={label} />;
    }},
  {
    key: 'plannedStartDate',
    header: 'Planned start',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? formatDate(String(v)) : undefined} />},
  {
    key: 'plannedDueDate',
    header: 'Planned due',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? formatDate(String(v)) : undefined} />},
  {
    key: 'snapshotEquipmentCode',
    header: 'Snap. eq. code',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'snapshotEquipmentName',
    header: 'Snap. eq. name',
    sortable: true,
    width: '10rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'snapshotEquipmentSerial',
    header: 'Snap. serial',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'snapshotJobCode',
    header: 'Snap. job code',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'snapshotJobTitle',
    header: 'Snap. job title',
    sortable: true,
    width: '11rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'description',
    header: 'Description',
    sortable: false,
    width: '12rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'completionNotes',
    header: 'Completion notes',
    sortable: false,
    width: '12rem',
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? formatDate(String(v)) : undefined} />},
  {
    key: 'updatedAt',
    header: 'Updated',
    sortable: true,
    width: '8rem',
    render: (v) => <TextCell value={v != null ? formatDate(String(v)) : undefined} />},
];

export const getDetailSections = (item: PmsWorkOrder, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      {
        label: 'Equipment',
        value: relName(item.equipment as { name?: string } | null, item.equipmentId as number | null)},
      {
        label: 'Instance',
        value: relName(
          item.equipmentInstance as { name?: string; serialNumber?: string } | null,
          item.equipmentInstanceId as number | null,
        )},
      {
        label: 'Job',
        value: relName(
          item.maintenanceJob as { title?: string; name?: string } | null,
          item.maintenanceJobId as number | null,
        )},
      {
        label: 'Schedule',
        value: item.maintenanceScheduleId != null ? `#${item.maintenanceScheduleId}` : '-'},
      { label: 'WO number', value: item.workOrderNumber != null ? String(item.workOrderNumber) : '-' },
      { label: 'Title', value: item.title != null ? String(item.title) : '-' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      {
        label: 'Status',
        value: relName(item.status as { name?: string; code?: string } | null, item.statusId as number | null)},
      {
        label: 'Source',
        value: relName(
          item.sourceType as { name?: string; code?: string } | null,
          item.sourceTypeId as number | null,
        )},
      {
        label: 'Approval',
        value: relName(
          item.approvalStatus as { name?: string; code?: string } | null,
          item.approvalStatusId as number | null,
        )},
      { label: 'Priority', value: item.priority != null ? String(item.priority) : '-' },
      {
        label: 'Assigned rank',
        value: relName(
          item.assignedRank as { name?: string; abrv?: string; code?: string } | null,
          item.assignedRankId as number | null,
        )},
      {
        label: 'Assigned user',
        value:
          (item.assignedUser as { userDisplayName?: string; userEmail?: string } | null)?.userDisplayName ||
          (item.assignedUser as { userEmail?: string } | null)?.userEmail ||
          (item.assignedUserId != null ? `#${item.assignedUserId}` : '-')},
      { label: 'Snapshot equipment', value: item.snapshotEquipmentName != null ? String(item.snapshotEquipmentName) : '-' },
      { label: 'Snapshot job', value: item.snapshotJobTitle != null ? String(item.snapshotJobTitle) : '-' },
      { label: 'Completion notes', value: item.completionNotes != null ? String(item.completionNotes) : '-' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsWorkOrder> => ({
  vesselId: undefined,
  equipmentId: undefined,
  equipmentInstanceId: undefined,
  maintenanceJobId: undefined,
  maintenanceScheduleId: undefined,
  workOrderNumber: '',
  title: '',
  description: '',
  statusId: undefined,
  sourceTypeId: undefined,
  approvalStatusId: undefined,
  priority: 3,
  assignedRankId: undefined,
  assignedUserId: undefined,
  snapshotEquipmentCode: '',
  snapshotEquipmentName: '',
  snapshotJobTitle: '',
  snapshotEquipmentSerial: '',
  snapshotJobCode: '',
  completionNotes: ''});
export const mapItemToFormData = (item: PmsWorkOrder): Partial<PmsWorkOrder> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  equipmentId: item.equipmentId != null ? Number(item.equipmentId) : undefined,
  equipmentInstanceId: item.equipmentInstanceId != null ? Number(item.equipmentInstanceId) : undefined,
  maintenanceJobId: item.maintenanceJobId != null ? Number(item.maintenanceJobId) : undefined,
  maintenanceScheduleId: item.maintenanceScheduleId != null ? Number(item.maintenanceScheduleId) : undefined,
  vessel: item.vessel ?? undefined,
  equipment: item.equipment ?? undefined,
  equipmentInstance: item.equipmentInstance ?? undefined,
  maintenanceJob: item.maintenanceJob ?? undefined,
  maintenanceSchedule: item.maintenanceSchedule ?? undefined,
  status: item.status ?? undefined,
  sourceType: item.sourceType ?? undefined,
  approvalStatus: item.approvalStatus ?? undefined,
  assignedRank: item.assignedRank ?? undefined,
  assignedUser: item.assignedUser ?? undefined,
  workOrderNumber: item.workOrderNumber != null ? String(item.workOrderNumber) : '',
  title: item.title != null ? String(item.title) : '',
  description: item.description != null ? String(item.description) : '',
  statusId: item.statusId != null ? Number(item.statusId) : undefined,
  sourceTypeId: item.sourceTypeId != null ? Number(item.sourceTypeId) : undefined,
  approvalStatusId: item.approvalStatusId != null ? Number(item.approvalStatusId) : undefined,
  priority: item.priority != null ? Number(item.priority) : 3,
  assignedRankId: item.assignedRankId != null ? Number(item.assignedRankId) : undefined,
  assignedUserId: item.assignedUserId != null ? Number(item.assignedUserId) : undefined,
  snapshotEquipmentCode: item.snapshotEquipmentCode != null ? String(item.snapshotEquipmentCode) : '',
  snapshotEquipmentName: item.snapshotEquipmentName != null ? String(item.snapshotEquipmentName) : '',
  snapshotJobTitle: item.snapshotJobTitle != null ? String(item.snapshotJobTitle) : '',
  snapshotEquipmentSerial:
    item.snapshotEquipmentSerial != null ? String(item.snapshotEquipmentSerial) : '',
  snapshotJobCode: item.snapshotJobCode != null ? String(item.snapshotJobCode) : '',
  completionNotes: item.completionNotes != null ? String(item.completionNotes) : ''});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
